<template>
  <div class="search-input">
    <div class="search-input__group">
      <span class="input-group-text">
        <slot name="icon">
          <img :src="`${ICONS_BASE}/search-icon.svg`" alt="" />
        </slot>
      </span>

      <input
        v-model="inputValue"
        type="text"
        id="residencyRequestsSearch"
        class="form-control"
        :placeholder="placeholder"
        autocomplete="off"
      />

      <button v-show="inputValue" type="button" class="search-input__clear" @click="clear">
        <slot name="clear-icon">
          <img :src="`${ICONS_BASE}/search-close-icon.svg`" alt="" />
        </slot>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount } from 'vue';
import { SEARCH_MIN_LENGTH } from '@/js/utils/search-input';

const props = withDefaults(
  defineProps<{
    modelValue?: string;
    placeholder?: string;
    debounce?: number;
  }>(),
  {
    modelValue: '',
    placeholder: 'Найти...',
    debounce: 400,
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
  (e: 'search', value: string): void;
}>();

const inputValue = ref(props.modelValue);

const ICONS_BASE =
  process.env.NODE_ENV === 'development'
    ? '/assets/images/icons'
    : '/local/templates/dist/assets/images/icons';

let timer: ReturnType<typeof setTimeout> | null = null;
let lastEmittedSearch = '';

watch(
  () => props.modelValue,
  (value) => {
    inputValue.value = value;
  },
);

watch(inputValue, (value) => {
  emit('update:modelValue', value);

  if (timer) {
    clearTimeout(timer);
  }

  timer = setTimeout(() => {
    const trimmed = value.trim();

    if (!trimmed) {
      if (lastEmittedSearch) {
        lastEmittedSearch = '';
        emit('search', '');
      }
      return;
    }

    if (trimmed.length >= SEARCH_MIN_LENGTH) {
      lastEmittedSearch = trimmed;
      emit('search', trimmed);
      return;
    }

    if (lastEmittedSearch.length >= SEARCH_MIN_LENGTH) {
      lastEmittedSearch = '';
      emit('search', '');
    }
  }, props.debounce);
});

function clear() {
  inputValue.value = '';
  lastEmittedSearch = '';
  emit('search', '');
}

onBeforeUnmount(() => {
  if (timer) {
    clearTimeout(timer);
  }
});
</script>

<style scoped lang="scss">
.search-input {
  width: 100%;

  &__group {
    display: flex;
    width: 100%;

    background: var(--stc-gray-25);
    border: 1px solid var(--stc-gray-100);
    border-radius: 8px;
    height: 40px;
    align-items: center;

    .input-group-text {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      background: transparent;
      border: none;
      padding: 8px;
      color: var(--stc-gray-300);

      img {
        width: 15px;
        height: 15px;
      }
    }

    .form-control {
      flex: 1;
      min-width: 0;

      background: transparent;
      border: none;
      outline: none;

      padding-left: 4px;
      padding-right: 4px;

      font-size: 14px;
      line-height: 160%;

      font-family: var(--stc-font-regular);
      color: var(--stc-black);

      &::placeholder {
        color: var(--stc-gray-300);
      }

      &:focus {
        box-shadow: none;
      }

      /* убирает встроенную иконку очистки в search input */
      &::-webkit-search-cancel-button {
        -webkit-appearance: none;
        display: none;
      }

      &::-webkit-search-decoration {
        -webkit-appearance: none;
      }
    }
  }

  &__clear {
    display: flex;
    align-items: center;
    justify-content: center;

    flex-shrink: 0;

    width: 32px;
    height: 32px;

    margin-right: 4px;
    padding: 0;

    background: transparent;
    border: none;
    border-radius: 6px;

    cursor: pointer;

    img {
      width: 16px;
      height: 16px;
    }

    &:hover {
      background: var(--stc-gray-50);
    }
  }

  &__highlight {
    display: inline;
    padding: 0 2px;
    border-radius: 2px;

    background-color: var(--stc-black);
    color: #fff;
  }
}
</style>
